import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Pagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { format } from 'date-fns';

interface AuditLog {
  id: string;
  activity: string;
  actionType: 'CREATED' | 'UPDATED' | 'DELETED' | 'LOGIN' | 'LOGOUT' | 'OTHER';
  date: string | Date;
  executedBy: string;
  executedByEmail: string;
  targetResource: string;
  targetResourceId: string | null;
  details: {
    description: string;
    changes: string | null;
    metadata?: any;
  };
  importance: 'high' | 'medium' | 'low';
  deviceName: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  deviceId: string | null;
}

// Helper function to calculate default date range: 1 week ago to today
const getDefaultDates = () => {
  const today = new Date();
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(today.getDate() - 7);
  
  // Format as YYYY-MM-DD for date input fields
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  return {
    startDate: formatDate(oneWeekAgo),
    endDate: formatDate(today)
  };
};

const ActivityLogPage: React.FC = () => {
  // Initialize with default 1-week date range
  const defaultDates = getDefaultDates();
  
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [actionType, setActionType] = useState<string>('');
  const [executedBy, setExecutedBy] = useState('');
  const [search, setSearch] = useState('');
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['auditLog', page, limit, startDate, endDate, actionType, executedBy, search],
    queryFn: async () => {
      const response = await apiClient.activity.getAuditLog({
        page,
        limit,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        actionType: actionType || undefined,
        executedBy: executedBy || undefined,
        search: search || undefined,
      });
      if (!response.data.success) {
        throw new Error('Failed to load audit log');
      }
      return response.data.data;
    },
  });

  const handleViewDetails = (log: AuditLog) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const handleClearFilters = () => {
    // Reset to default 1-week range
    const defaultDates = getDefaultDates();
    setStartDate(defaultDates.startDate);
    setEndDate(defaultDates.endDate);
    setActionType('');
    setExecutedBy('');
    setSearch('');
    setPage(1);
  };

  const getImportanceColor = (importance: string) => {
    switch (importance) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getActionTypeColor = (actionType: string) => {
    switch (actionType) {
      case 'DELETED':
        return 'error';
      case 'UPDATED':
        return 'warning';
      case 'CREATED':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          Activity Log
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View all system activities, including who performed actions and when
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Action Type</InputLabel>
                <Select
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                  label="Action Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="CREATED">Created</MenuItem>
                  <MenuItem value="UPDATED">Updated</MenuItem>
                  <MenuItem value="DELETED">Deleted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Executed By"
                value={executedBy}
                onChange={(e) => setExecutedBy(e.target.value)}
                placeholder="Name or email"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search activities..."
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                size="small"
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load activity log. Please try again.
        </Alert>
      ) : (
        <>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Audit Log Entries
                  {data?.pagination.total !== undefined && (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({data.pagination.total} total)
                    </Typography>
                  )}
                </Typography>
              </Box>

              {data?.logs && data.logs.length > 0 ? (
                <>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Date & Time</strong></TableCell>
                          <TableCell><strong>Activity</strong></TableCell>
                          <TableCell><strong>Action Type</strong></TableCell>
                          <TableCell><strong>Executed By</strong></TableCell>
                          <TableCell><strong>Target</strong></TableCell>
                          <TableCell><strong>Importance</strong></TableCell>
                          <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.logs.map((log: AuditLog) => {
                          const dateValue = log.date ? new Date(log.date) : null;
                          const isValidDate = dateValue && !isNaN(dateValue.getTime());
                          
                          return (
                            <TableRow key={log.id} hover>
                              <TableCell>
                                {isValidDate ? format(dateValue, 'MMM dd, yyyy HH:mm:ss') : 'Invalid Date'}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {log.details.description || log.activity}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {log.targetResource}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={log.actionType}
                                  color={getActionTypeColor(log.actionType) as any}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{log.executedBy}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {log.executedByEmail}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{log.targetResource}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={log.importance.toUpperCase()}
                                  color={getImportanceColor(log.importance) as any}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewDetails(log)}
                                  title="View Details"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Per Page</InputLabel>
                      <Select
                        value={limit}
                        onChange={(e) => {
                          setLimit(Number(e.target.value));
                          setPage(1);
                        }}
                        label="Per Page"
                      >
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                    <Pagination
                      count={data.pagination?.totalPages || 0}
                      page={page}
                      onChange={(_, newPage) => setPage(newPage)}
                      color="primary"
                    />
                  </Box>
                </>
              ) : (
                <Alert severity="info">No audit log entries found.</Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Activity Details</DialogTitle>
        <DialogContent>
          {selectedLog && (() => {
            const dateValue = selectedLog.date ? new Date(selectedLog.date) : null;
            const isValidDate = dateValue && !isNaN(dateValue.getTime());
            
            return (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Activity</Typography>
                    <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>{selectedLog.activity}</Typography>

                    <Typography variant="caption" color="text.secondary">Executed By</Typography>
                    <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                      {selectedLog.executedBy} ({selectedLog.executedByEmail})
                    </Typography>

                    <Typography variant="caption" color="text.secondary">Resource Type</Typography>
                    <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>{selectedLog.targetResource}</Typography>

                    <Typography variant="caption" color="text.secondary">Importance</Typography>
                    <Box sx={{ mt: 0.5, mb: 2 }}>
                      <Chip
                        label={selectedLog.importance.toUpperCase()}
                        color={getImportanceColor(selectedLog.importance) as any}
                        size="small"
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                      {selectedLog.details.description}
                    </Typography>

                    {selectedLog.details.changes && (
                      <>
                        <Typography variant="caption" color="text.secondary">Changes</Typography>
                        <Paper variant="outlined" sx={{ p: 2, mt: 1, mb: 2 }}>
                          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', margin: 0 }}>
                            {selectedLog.details.changes}
                          </Typography>
                        </Paper>
                      </>
                    )}
                  </Grid>

                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Date & Time</Typography>
                    <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                      {isValidDate ? format(dateValue, 'MMM dd, yyyy HH:mm:ss') : 'Invalid Date'}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">Action Type</Typography>
                    <Box sx={{ mt: 0.5, mb: 2 }}>
                      <Chip
                        label={selectedLog.actionType}
                        color={getActionTypeColor(selectedLog.actionType) as any}
                        size="small"
                      />
                    </Box>

                    <Typography variant="caption" color="text.secondary">Resource Information</Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedLog.details.description || selectedLog.targetResource || 'N/A'}
                    </Typography>
                    {selectedLog.targetResourceId && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                        ID: {selectedLog.targetResourceId}
                      </Typography>
                    )}

                    {/* Device Information Section */}
                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Device Information
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Device Name</Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedLog.deviceName || 'Unknown Device'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>IP Address</Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedLog.ipAddress || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Device ID</Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedLog.deviceId || 'Unknown'}
                      </Typography>
                      {selectedLog.userAgent && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>User Agent</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {selectedLog.userAgent}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                  
                  {selectedLog.details.metadata && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Metadata</Typography>
                      <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                        <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                          {JSON.stringify(selectedLog.details.metadata, null, 2)}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ActivityLogPage;

